import { store } from "@/store";
import { RealtimeClient } from "@supabase/realtime-js";
import { useToast } from "vue-toastification";
import i18n from "@/i18n";

const debug = process.env.NODE_ENV !== "production";

// handler functions
const timeSheetHandler = (realtimeData) => {
  if (debug) {
    console.log("timeSheetHandler", realtimeData);
  }
  try {
    realtimeData.date = realtimeData.date + "T00:00:00Z";
    realtimeData.flights = realtimeData.flights
      ? JSON.parse(realtimeData.flights)
      : null;
    store.dispatch("timeSheets/updateTimeSheet", {
      timeSheet: realtimeData,
    });
  } catch (e) {
    console.log(e);
    return false;
  }
};

const flightOvertakeHandler = (realtimeData, isDelete = false) => {
  if (debug) {
    console.log("flightOvertakeHandler", realtimeData, isDelete);
  }
  const flight = store.getters["timeSheets/flightById"](
    realtimeData.source_flight_id
  );
  let message = i18n.global.t("message.newFlightOvertake", {
    number: flight.number,
    holeNumber: realtimeData.overtaking_hole_number,
  });
  if (isDelete) {
    message = i18n.global.t("message.undoFlightOvertake", {
      number: flight.number,
      holeNumber: realtimeData.overtaking_hole_number,
    });
  }
  useToast().info(message);
  try {
    store.dispatch(
      "timeSheets/reloadTimeSheet",
      realtimeData.time_sheet_id
    );
  } catch (e) {
    console.log(e);
    return false;
  }
};

const delayHandler = (realtimeData, isDelete = false) => {
  if (debug) {
    console.log("delayHandler", realtimeData, isDelete);
  }
  let message = i18n.global.t("message.newDelay");
  if (isDelete) {
    message = i18n.global.t("message.removedDelay");
  }
  useToast().error(message);
  try {
    store.dispatch(
      "timeSheets/reloadTimeSheet",
      realtimeData.time_sheet_id
    );
  } catch (e) {
    console.log(e);
    return false;
  }
};

const refereeDecisionHandler = (realtimeData, isDelete = false) => {
  if (debug) {
    console.log("refereeDecisionHandler", realtimeData, isDelete);
  }
  const flight = store.getters["timeSheets/flightById"](realtimeData.flight_id);
  let message = i18n.global.t("message.newRefereeDecision", {
    number: flight.number,
    holeNumber: realtimeData.hole_number,
  });
  if (isDelete) {
    message = i18n.global.t("message.removedRefereeDecision", {
      number: flight.number,
      holeNumber: realtimeData.hole_number,
    });
  }
  useToast().error(message);

  const action = `timeSheets/${
    isDelete ? "removeRefereeDecision" : "updateRefereeDecision"
  }`;

  try {
    store.dispatch(action, {
      id: realtimeData.id,
      timeSheetId: realtimeData.time_sheet_id,
      flightId: realtimeData.flight_id,
      holeNumber: realtimeData.hole_number,
      rule: realtimeData.rule,
      player: realtimeData.player,
      description: realtimeData.description,
      userName: realtimeData.user_name,
      userInitials: realtimeData.user_initials,
      createdAt: realtimeData.created_at,
      updatedAt: realtimeData.update_at,
    });
  } catch (e) {
    console.log(e);
    return false;
  }
};

const offsetsHandler = (realtimeData, isDelete = false) => {
  if (debug) {
    console.log("offsetsHandler", realtimeData, isDelete);
  }

  const action = `timeSheets/${isDelete ? "deleteOffset" : "updateOffset"}`;

  try {
    store.dispatch(action, {
      timeSheetId: realtimeData.time_sheet_id,
      holeNumber: realtimeData.hole_number * 1,
      flightId: realtimeData.flight_id,
      value: realtimeData.value * 1,
      userName: realtimeData.user_name,
      userInitials: realtimeData.user_initials,
    });
  } catch (e) {
    console.log(e);
    return false;
  }
};

export const realtimeClientMixin = {
  data() {
    return {
      webSocketClient: new RealtimeClient(
        process.env.VUE_APP_MARSHALL_SERVICE_SOCKET_URL
      ),
      offsetsChannels: {},
      timeSheetChannels: {},
      flightOvertakeChannels: {},
      delayChannels: {},
      refereeDecisionChannels: {},
    };
  },
  methods: {
    async connectWebSocket() {
      await this.webSocketClient.connect();
    },
    unsubscribeChannels() {
      // eslint-disable-next-line no-unused-vars
      for (const [key, value] of Object.entries(this.offsetsChannels)) {
        value.unsubscribe();
        delete this.offsetsChannels[key];
      }
      // eslint-disable-next-line no-unused-vars
      for (const [key, value] of Object.entries(this.timeSheetChannels)) {
        value.unsubscribe();
        delete this.timeSheetChannels[key];
      }
      // eslint-disable-next-line no-unused-vars
      for (const [key, value] of Object.entries(this.flightOvertakeChannels)) {
        value.unsubscribe();
        delete this.flightOvertakeChannels[key];
      }
      // eslint-disable-next-line no-unused-vars
      for (const [key, value] of Object.entries(this.delayChannels)) {
        value.unsubscribe();
        delete this.delayChannels[key];
      }
      // eslint-disable-next-line no-unused-vars
      for (const [key, value] of Object.entries(this.refereeDecisionChannels)) {
        value.unsubscribe();
        delete this.refereeDecisionChannels[key];
      }
    },
    async subscribeChannels(timeSheetIds) {
      if (this.webSocketClient == null) {
        await this.connectWebSocket();
      }

      this.unsubscribeChannels();

      setTimeout(() => {
        timeSheetIds.forEach((timeSheetId) => {
          // subscribe to offsets changes (CRUD)
          this.offsetsChannels[timeSheetId] = this.webSocketClient.channel(
            "realtime:public:time_sheet_hole_offsets:time_sheet_id=eq." +
              timeSheetId
          );
          this.offsetsChannels[timeSheetId].on("INSERT", (element) =>
            offsetsHandler(element.record, false)
          );
          this.offsetsChannels[timeSheetId].on("UPDATE", (element) =>
            offsetsHandler(element.record, false)
          );
          this.offsetsChannels[timeSheetId].on("DELETE", (element) =>
            offsetsHandler(element.old_record, true)
          );
          this.offsetsChannels[timeSheetId].subscribe();

          // subscribe to timeSheet updates
          this.timeSheetChannels[timeSheetId] = this.webSocketClient.channel(
            "realtime:public:time_sheets:id=eq." + timeSheetId
          );
          this.timeSheetChannels[timeSheetId].on("UPDATE", (element) =>
            timeSheetHandler(element.record)
          );
          this.timeSheetChannels[timeSheetId].subscribe();

          // subscribe to flight overtake changes (CRUD)
          this.flightOvertakeChannels[timeSheetId] =
            this.webSocketClient.channel(
              "realtime:public:flight_overtakes:time_sheet_id=eq." + timeSheetId
            );
          this.flightOvertakeChannels[timeSheetId].on("INSERT", (element) =>
            flightOvertakeHandler(element.record)
          );
          this.flightOvertakeChannels[timeSheetId].on("UPDATE", (element) =>
            flightOvertakeHandler(element.record)
          );
          this.flightOvertakeChannels[timeSheetId].on("DELETE", (element) =>
            flightOvertakeHandler(element.old_record, true)
          );
          this.flightOvertakeChannels[timeSheetId].subscribe();

          // subscribe to flight overtake changes (CRUD)
          this.delayChannels[timeSheetId] = this.webSocketClient.channel(
            "realtime:public:time_sheet_delays:time_sheet_id=eq." + timeSheetId
          );
          this.delayChannels[timeSheetId].on("INSERT", (element) =>
            delayHandler(element.record)
          );
          this.delayChannels[timeSheetId].on("UPDATE", (element) =>
            delayHandler(element.record)
          );
          this.delayChannels[timeSheetId].on("DELETE", (element) =>
            delayHandler(element.old_record, true)
          );
          this.delayChannels[timeSheetId].subscribe();

          // subscribe to flight overtake changes (CRUD)
          this.refereeDecisionChannels[timeSheetId] =
            this.webSocketClient.channel(
              "realtime:public:referee_decisions:time_sheet_id=eq." +
                timeSheetId
            );
          this.refereeDecisionChannels[timeSheetId].on("INSERT", (element) =>
            refereeDecisionHandler(element.record)
          );
          this.refereeDecisionChannels[timeSheetId].on("UPDATE", (element) =>
            refereeDecisionHandler(element.record)
          );
          this.refereeDecisionChannels[timeSheetId].on("DELETE", (element) =>
            refereeDecisionHandler(element.old_record, true)
          );
          this.refereeDecisionChannels[timeSheetId].subscribe();
        });
      }, 1000);
    },
  },
};
